import { isSupabaseConfigured } from '../lib/env';
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { createUserWithRole, sendApprovalEmail } from '../utils/callEdge';
import type {
  AdvisorRequest,
  AdvisorManagement,
  AdvisorRequestsStats,
  Territory,
  AdvisorSector,
  AdvisorSignupForm,
  AdvisorApprovalForm,
  AdvisorRejectionForm,
  AdvisorStatusUpdateForm,
  AdvisorFilters,
  AdvisorRequestStatus,
  AdvisorStatus
} from '../types/advisor';

interface AdvisorState {
  // Data
  advisors: AdvisorManagement[];
  requests: AdvisorRequest[];
  currentAdvisor: AdvisorManagement | null;
  currentRequest: AdvisorRequest | null;
  territories: Territory[];
  sectors: AdvisorSector[];
  stats: AdvisorRequestsStats | null;
  
  // UI State
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchAdvisors: (filters?: AdvisorFilters) => Promise<void>;
  fetchAdvisorRequests: (status?: AdvisorRequestStatus) => Promise<void>;
  fetchAdvisor: (id: string) => Promise<void>;
  fetchRequest: (id: string) => Promise<void>;
  fetchTerritories: () => Promise<void>;
  fetchSectors: () => Promise<void>;
  fetchStats: () => Promise<void>;
  
  // Advisor Management
  approveRequest: (id: string, data: AdvisorApprovalForm) => Promise<void>;
  rejectRequest: (id: string, data: AdvisorRejectionForm) => Promise<void>;
  suspendAdvisor: (id: string, data: AdvisorStatusUpdateForm) => Promise<void>;
  activateAdvisor: (id: string) => Promise<void>;
  
  // Self-signup
  submitAdvisorRequest: (data: AdvisorSignupForm) => Promise<void>;
  
  // Utilities
  clearError: () => void;
  setCurrentAdvisor: (advisor: AdvisorManagement | null) => void;
  setCurrentRequest: (request: AdvisorRequest | null) => void;
}

export const useAdvisorStore = create<AdvisorState>((set, get) => ({
  // Initial state
  advisors: [],
  requests: [],
  currentAdvisor: null,
  currentRequest: null,
  territories: [],
  sectors: [],
  stats: null,
  isLoading: false,
  error: null,

  // Fetch advisors with filters
  fetchAdvisors: async (filters?: AdvisorFilters) => {
    set({ isLoading: true, error: null });
    try {
      if (!isSupabaseConfigured()) {
        set({ advisors: [], isLoading: false });
        return;
      }

      let query = supabase
        .from('users')
        .select(`
          id,
          email,
          full_name,
          phone,
          advisor_status,
          organization,
          territory_id,
          territories (
            id,
            name
          ),
          created_at
        `)
        .eq('profile_type', 'ACCOMPAGNATEUR');

      if (filters?.status && filters.status !== 'ALL') {
        query = query.eq('advisor_status', filters.status);
      }

      if (filters?.territory_id) {
        query = query.eq('territory_id', filters.territory_id);
      }

      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        query = query.or(`full_name.ilike.%${searchLower}%,email.ilike.%${searchLower}%`);
      }

      const sortField = filters?.sort || 'created_at';
      const ascending = filters?.order === 'asc';

      switch (sortField) {
        case 'name':
          query = query.order('full_name', { ascending });
          break;
        case 'projects':
          query = query.order('created_at', { ascending });
          break;
        default:
          query = query.order('created_at', { ascending });
      }

      const { data: advisorsData, error: advisorsError } = await query;

      if (advisorsError) throw advisorsError;

      const advisors: AdvisorManagement[] = await Promise.all(
        (advisorsData || []).map(async (advisor) => {
          const { count: activeProjects } = await supabase
            .from('projects')
            .select('*', { count: 'exact', head: true })
            .eq('assigned_advisor_id', advisor.id)
            .in('status', ['EN_COURS', 'SOUMIS', 'ACCORD_PROVISOIRE']);

          const { count: completedProjects } = await supabase
            .from('projects')
            .select('*', { count: 'exact', head: true })
            .eq('assigned_advisor_id', advisor.id)
            .in('status', ['VALIDE', 'TERMINE']);

          const { data: completionData } = await supabase
            .from('projects')
            .select('completion_percentage')
            .eq('assigned_advisor_id', advisor.id)
            .not('completion_percentage', 'is', null);

          const avgCompletion = completionData && completionData.length > 0
            ? completionData.reduce((sum, p) => sum + (p.completion_percentage || 0), 0) / completionData.length
            : 0;

          return {
            id: advisor.id,
            full_name: advisor.full_name || 'Sans nom',
            email: advisor.email,
            phone: advisor.phone,
            advisor_status: advisor.advisor_status as AdvisorStatus,
            organization: advisor.organization,
            territory_id: advisor.territory_id,
            territory_name: advisor.territories?.name,
            current_projects_count: activeProjects || 0,
            completed_projects_count: completedProjects || 0,
            avg_completion_rate: avgCompletion,
            created_at: advisor.created_at
          };
        })
      );

      set({ advisors, isLoading: false });
    } catch (error: any) {
      console.error('[ADVISOR_STORE] Error fetching advisors:', error);
      set({ error: error.message, isLoading: false });
    }
  },

  // Fetch advisor requests
  fetchAdvisorRequests: async (status?: AdvisorRequestStatus) => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from('advisor_requests')
        .select(`
          *,
          processor:users!advisor_requests_processed_by_fkey(id, full_name, email)
        `)
        .order('created_at', { ascending: false });

      // Apply status filter
      if (status) {
        query = query.eq('status', status.toLowerCase());
      }

      const { data, error } = await query;

      if (error) throw error;

      set({ requests: data || [], isLoading: false });
    } catch (error: any) {
      console.error('Error fetching advisor requests:', error);
      set({ error: error.message, isLoading: false });
    }
  },

  // Fetch single advisor
  fetchAdvisor: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('advisor_management_view')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      set({ currentAdvisor: data, isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  // Fetch single request
  fetchRequest: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('advisor_requests')
        .select(`
          *,
          processor:users!advisor_requests_processed_by_fkey(id, full_name, email)
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      set({ currentRequest: data, isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  // Fetch territories
  fetchTerritories: async () => {
    try {
      if (!isSupabaseConfigured()) {
        // Mock territories for development
        set({ territories: [
          { id: '1', name: 'Grand Sud Caraïbe', description: 'Territoire du Grand Sud Caraïbe', is_active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
          { id: '2', name: 'Nord Grande Terre', description: 'Territoire du Nord Grande Terre', is_active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
          { id: '3', name: 'Riviera du Levant', description: 'Territoire de la Riviera du Levant', is_active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
          { id: '4', name: 'Marie-Galante', description: 'Territoire de Marie-Galante', is_active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
          { id: '5', name: 'Cap Excellence', description: 'Territoire de Cap Excellence', is_active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
          { id: '6', name: 'Nord Basse-Terre', description: 'Territoire du Nord Basse-Terre', is_active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
        ] });
        return;
      }

      const { data, error } = await supabase
        .from('territories')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Error fetching territories:', error);
        // Fallback to mock data
        set({ territories: [
          { id: '1', name: 'Grand Sud Caraïbe', description: 'Territoire du Grand Sud Caraïbe', is_active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
          { id: '2', name: 'Nord Grande Terre', description: 'Territoire du Nord Grande Terre', is_active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
        ] });
        return;
      }

      set({ territories: data || [] });
    } catch (error: any) {
      console.error('Error fetching territories:', error);
      set({ territories: [] });
    }
  },

  // Fetch sectors
  fetchSectors: async () => {
    try {
      if (!isSupabaseConfigured()) {
        // Mock sectors for development
        set({ sectors: [
          { id: '1', name: 'Agriculture Biologique', code: 'AGRI_BIO', description: 'Agriculture bio et circuits courts', is_active: true },
          { id: '2', name: 'Artisanat Local', code: 'ARTISANAT', description: 'Artisanat traditionnel guadeloupéen', is_active: true },
          { id: '3', name: 'Commerce Équitable', code: 'COMMERCE_EQ', description: 'Commerce équitable et solidaire', is_active: true },
          { id: '4', name: 'Services à la Personne', code: 'SERVICES_PERS', description: 'Aide à domicile, services aux seniors', is_active: true },
          { id: '5', name: 'Tourisme Solidaire', code: 'TOURISME_SOL', description: 'Tourisme responsable et écologique', is_active: true }
        ] });
        return;
      }

      const { data, error } = await supabase
        .from('advisor_sectors')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Error fetching sectors:', error);
        // Fallback to mock data
        set({ sectors: [
          { id: '1', name: 'Agriculture Biologique', code: 'AGRI_BIO', description: 'Agriculture bio', is_active: true },
          { id: '2', name: 'Artisanat Local', code: 'ARTISANAT', description: 'Artisanat traditionnel', is_active: true }
        ] });
        return;
      }

      set({ sectors: data || [] });
    } catch (error: any) {
      console.error('Error fetching sectors:', error);
      set({ sectors: [] });
    }
  },

  // Fetch stats
  fetchStats: async () => {
    try {
      // Utiliser la vraie fonction RPC
      const { data, error } = await supabase.rpc('get_advisor_requests_stats').single();

      if (error) {
        console.error('Error fetching advisor requests stats:', error);
        // En cas d'erreur, calculer manuellement
        const { data: requests, error: requestsError } = await supabase
          .from('advisor_requests')
          .select('status, created_at, full_name, email');

        if (!requestsError && requests) {
          const stats = {
            pending_count: requests.filter(r => r.status === 'pending').length,
            approved_count: requests.filter(r => r.status === 'approved').length,
            rejected_count: requests.filter(r => r.status === 'rejected').length,
            total_count: requests.length,
            recent_requests: requests.slice(0, 5).map(r => ({
              id: crypto.randomUUID(),
              full_name: r.full_name,
              email: r.email,
              created_at: r.created_at
            }))
          };
          set({ stats });
        } else {
          set({ stats: { pending_count: 0, approved_count: 0, rejected_count: 0, total_count: 0, recent_requests: [] } });
        }
        return;
      }

      // Adapter le format des données reçues
      if (data) {
        const adaptedStats = {
          pending_count: data.pending || 0,
          approved_count: data.approved || 0,
          rejected_count: data.rejected || 0,
          total_count: data.total || 0,
          recent_requests: []
        };
        set({ stats: adaptedStats });
      }
    } catch (error: any) {
      console.error('Error fetching stats:', error);
      set({ stats: { pending_count: 0, approved_count: 0, rejected_count: 0, total_count: 0, recent_requests: [] } });
    }
  },

  // Approve request
  approveRequest: async (id: string, approvalData: AdvisorApprovalForm) => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get the request details first to know the requested role
      const { data: request, error: requestError } = await supabase
        .from('advisor_requests')
        .select('*')
        .eq('id', id)
        .single();

      if (requestError) throw requestError;
      if (!request) throw new Error('Request not found');

      // Check if user already exists in auth.users
      const { data: existingUserId, error: checkError } = await supabase
        .rpc('check_email_exists', { user_email: request.email });

      if (checkError) {
        console.error('[APPROVE] Error checking email:', checkError);
      }

      let targetUserId: string;

      if (existingUserId) {
        // User already exists in auth.users, just use their ID
        console.log('[APPROVE] User already exists:', existingUserId);
        targetUserId = existingUserId;

        // Check if profile exists in users table
        const { data: existingProfile } = await supabase
          .from('users')
          .select('id')
          .eq('id', targetUserId)
          .maybeSingle();

        // If profile doesn't exist, create it using RPC function
        if (!existingProfile) {
          const { error: profileError } = await supabase.rpc('create_user_profile', {
            p_user_id: targetUserId,
            p_email: request.email.trim().toLowerCase(),
            p_profile_type: request.role.toUpperCase(),
            p_full_name: request.full_name,
            p_phone: request.phone,
            p_organization: request.organization,
            p_advisor_status: 'PENDING'
          });

          if (profileError) {
            console.error('[APPROVE] Error creating profile:', profileError);
            throw new Error('Failed to create user profile');
          }
        }
      } else {
        // User doesn't exist, create new one
        console.log('[APPROVE] Creating new user:', request.email);

        // Decode password from base64url
        const password = atob(request.password_hash.replace(/-/g, '+').replace(/_/g, '/'));

        // Create the user using edge function
        const { userId, error: createError } = await createUserWithRole(
          request.email,
          password,
          {
            role: request.role.toLowerCase(),
            metadata: {
              full_name: request.full_name,
              phone: request.phone,
              organization: request.organization
            },
            profile_data: {
              profile_type: request.role.toUpperCase(),
              full_name: request.full_name,
              phone: request.phone,
              organization: request.organization
            }
          }
        );

        if (createError) throw new Error(createError);
        if (!userId) throw new Error('User creation failed - no user ID returned');

        targetUserId = userId;
      }

      // Update advisor-specific fields in the user profile
      const updateData: any = {
        advisor_status: 'ACTIVE',
        advisor_approved_by: user.id,
        advisor_approved_at: new Date().toISOString()
      };

      // Add territory_id if provided
      if (approvalData.territory_id) {
        updateData.territory_id = approvalData.territory_id;
      }

      const { error: updateError } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', targetUserId);

      if (updateError) throw updateError;

      // Update the request status
      const { error } = await supabase
        .from('advisor_requests')
        .update({
          status: 'approved',
          processed_by: user.id,
          processed_at: new Date().toISOString(),
          notes: approvalData.notes
        })
        .eq('id', id);

      if (error) throw error;

      // Send approval email to the user
      try {
        // Get territory name if territory_id is set
        let territoryName: string | undefined;
        if (approvalData.territory_id) {
          const { data: territory } = await supabase
            .from('territories')
            .select('name')
            .eq('id', approvalData.territory_id)
            .maybeSingle();

          territoryName = territory?.name;
        }

        const { error: emailError } = await sendApprovalEmail({
          email: request.email,
          full_name: request.full_name,
          role: request.role,
          organization: request.organization,
          territory_name: territoryName
        });

        if (emailError) {
          console.error('Failed to send approval email:', emailError);
        } else {
          console.log('Approval email sent successfully to:', request.email);
        }
      } catch (emailError) {
        console.error('Exception sending approval email:', emailError);
      }

      // Notify coordinators if requested
      if (approvalData.notify_coordinators) {
        try {
          // Get all coordinators
          const { data: coordinators } = await supabase
            .from('users')
            .select('id, email, full_name')
            .eq('profile_type', 'COORDINATEUR');

          if (coordinators && coordinators.length > 0) {
            // Create notifications for all coordinators
            const notifications = coordinators.map(coordinator => ({
              user_id: coordinator.id,
              title: 'Nouvel accompagnateur approuvé',
              message: `${request.full_name} (${request.email}) a été approuvé comme accompagnateur${request.organization ? ` pour ${request.organization}` : ''}.`,
              type: 'info',
              is_read: false,
              action_url: '/admin/accompagnateurs',
              created_at: new Date().toISOString()
            }));

            await supabase
              .from('notifications')
              .insert(notifications);
          }
        } catch (notifError) {
          console.error('Failed to send notifications:', notifError);
          // Don't fail the approval if notifications fail
        }
      }

      // Refresh data
      await get().fetchAdvisorRequests();
      await get().fetchStats();

      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Reject request
  rejectRequest: async (id: string, rejectionData: AdvisorRejectionForm) => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get the request details first
      const { data: request, error: requestError } = await supabase
        .from('advisor_requests')
        .select('*')
        .eq('id', id)
        .single();

      if (requestError) throw requestError;
      if (!request) throw new Error('Request not found');

      const { error } = await supabase
        .from('advisor_requests')
        .update({
          status: 'rejected',
          processed_by: user.id,
          processed_at: new Date().toISOString(),
          rejection_reason: rejectionData.rejection_reason
        })
        .eq('id', id);

      if (error) throw error;

      // Notify coordinators if requested
      if (rejectionData.notify_coordinators) {
        try {
          // Get all coordinators
          const { data: coordinators } = await supabase
            .from('users')
            .select('id, email, full_name')
            .eq('profile_type', 'COORDINATEUR');

          if (coordinators && coordinators.length > 0) {
            // Create notifications for all coordinators
            const notifications = coordinators.map(coordinator => ({
              user_id: coordinator.id,
              title: 'Demande accompagnateur refusée',
              message: `La demande de ${request.full_name} (${request.email}) a été refusée. Raison: ${rejectionData.rejection_reason}`,
              type: 'warning',
              is_read: false,
              action_url: '/admin/accompagnateurs/requests',
              created_at: new Date().toISOString()
            }));

            await supabase
              .from('notifications')
              .insert(notifications);
          }
        } catch (notifError) {
          console.error('Failed to send notifications:', notifError);
          // Don't fail the rejection if notifications fail
        }
      }

      // Refresh data
      await get().fetchAdvisorRequests();
      await get().fetchStats();

      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Suspend advisor
  suspendAdvisor: async (id: string, data: AdvisorStatusUpdateForm) => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase.rpc('update_advisor_status', {
        advisor_id: id,
        new_status: 'SUSPENDED',
        reason: data.reason,
        updated_by: user.id
      });

      if (error) throw error;

      // Refresh data
      await get().fetchAdvisors();
      
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Activate advisor
  activateAdvisor: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase.rpc('update_advisor_status', {
        advisor_id: id,
        new_status: 'ACTIVE',
        updated_by: user.id
      });

      if (error) throw error;

      // Refresh data
      await get().fetchAdvisors();
      
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Submit advisor request (self-signup)
  submitAdvisorRequest: async (signupData: AdvisorSignupForm) => {
    set({ isLoading: true, error: null });
    try {
      // Encode password to base64url to satisfy database NOT NULL constraint
      const passwordHash = btoa(signupData.password)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

      // Create advisor request directly (no auth user creation)
      const { error: requestError } = await supabase
        .from('advisor_requests')
        .insert({
          password_hash: passwordHash,
          email: signupData.email.trim().toLowerCase(),
          full_name: signupData.full_name,
          phone: signupData.phone,
          organization: signupData.organization,
          role: signupData.role || 'ACCOMPAGNATEUR',
          status: 'pending',
          experience: signupData.experience,
          motivation: signupData.motivation
        });

      if (requestError) {
        // Check if it's a duplicate email error
        if (requestError.code === '23505' && requestError.message.includes('advisor_requests_email_key')) {
          const friendlyError = new Error('Cet email a déjà été utilisé pour une demande. Si vous avez déjà soumis une demande, veuillez attendre son traitement par un administrateur.');
          set({ error: friendlyError.message, isLoading: false });
          throw friendlyError;
        }
        throw requestError;
      }

      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Utilities
  clearError: () => set({ error: null }),
  setCurrentAdvisor: (advisor) => set({ currentAdvisor: advisor }),
  setCurrentRequest: (request) => set({ currentRequest: request })
}));